import React from "react";
import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import {
  MARKETING_FOOTER_COLUMNS,
  MARKETING_FOOTER_LEGAL_LINKS,
  MARKETING_SOCIAL_LINKS,
} from "../data/marketingFooterNav";

/** Release notes route (WHATS_NEW_PATH in WhatsNewPage.js, which imports this footer). */
const WHATS_NEW_LINK = { label: "What's new", to: "/whats-new" };

const SOCIAL_ICONS = [LinkedInIcon, TwitterIcon, FacebookIcon];

/**
 * Renders a single footer link (in-app route or external URL).
 *
 * @param {object} props
 * @param {import("../data/marketingFooterNav").MarketingFooterLink} props.link
 * @returns {React.ReactElement}
 */
function FooterLink({ link }) {
  if (link.to) {
    return (
      <MuiLink
        component={RouterLink}
        to={link.to}
        variant="body2"
        color="text.secondary"
        underline="hover"
        sx={{
          display: "block",
          transition: "color 0.15s ease",
          "&:hover": { color: "secondary.main" },
        }}
      >
        {link.label}
      </MuiLink>
    );
  }
  if (link.href) {
    return (
      <MuiLink
        href={link.href}
        variant="body2"
        color="text.secondary"
        underline="hover"
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: "block",
          transition: "color 0.15s ease",
          "&:hover": { color: "secondary.main" },
        }}
      >
        {link.label}
      </MuiLink>
    );
  }
  return null;
}

/**
 * Shared footer for landing and marketing subpages (wired links, no dead Typography).
 *
 * @returns {React.ReactElement}
 */
const MarketingSiteFooter = () => {
  return (
    <Box
      component="footer"
      sx={{ py: 8, backgroundColor: "background.default", borderTop: "1px solid", borderColor: "divider" }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <img src="/QuillAI-Logo.png" alt="Quill AI logo" style={{ height: 32, marginRight: 8 }} />
              <Typography variant="h6" fontWeight={700}>
                Quill AI
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The AI document workspace for proposals, SOWs, and delivery artifacts, grounded in your meetings and
              templates.
            </Typography>
            <Stack direction="row" spacing={1}>
              {MARKETING_SOCIAL_LINKS.map((social, index) => {
                const Icon = SOCIAL_ICONS[index] || LinkedInIcon;
                return (
                  <IconButton
                    key={social.label}
                    component="a"
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    color="inherit"
                    aria-label={social.label}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                );
              })}
            </Stack>
          </Grid>
          {MARKETING_FOOTER_COLUMNS.map((column) => (
            <Grid item xs={12} md={3} key={column.title}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                {column.title}
              </Typography>
              <Stack spacing={1.5}>
                {column.links.map((link) => (
                  <FooterLink key={link.label} link={link} />
                ))}
              </Stack>
            </Grid>
          ))}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              Legal &amp; Social
            </Typography>
            <Stack spacing={1.5}>
              {MARKETING_FOOTER_LEGAL_LINKS.map((link) => (
                <FooterLink key={link.label} link={link} />
              ))}
            </Stack>
          </Grid>
        </Grid>
        <Divider sx={{ my: 4 }} />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1, sm: 3 }}
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} Quill AI. All rights reserved.
          </Typography>
          <FooterLink link={WHATS_NEW_LINK} />
        </Stack>
      </Container>
    </Box>
  );
};

export default MarketingSiteFooter;
